import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const INTERESTS = [
  "Gestione continua",
  "Partenza assistita",
  "Un'ora con Luciano",
  "Vengo a vederti",
  "Analisi di apertura",
  "Strategia prezzi",
  "Creare l'annuncio",
  "Foto che vendono",
  "Check-up del tuo annuncio",
  "Candidatura struttura",
  "Altro",
];

const inputCls =
  "w-full rounded-2xl border border-[#383029] bg-transparent px-5 py-3.5 text-sm text-[#FAF7F2] placeholder-[#A89F91] outline-none transition-colors duration-300 focus:border-[#E05A2C]";

export default function LeadModal() {
  const [open, setOpen] = useState(false);
  const [interest, setInterest] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const handler = (e) => {
      setInterest(INTERESTS.includes(e.detail) ? e.detail : e.detail || "Altro");
      setDone(false);
      setError("");
      setOpen(true);
    };
    window.addEventListener("open-lead-modal", handler);
    return () => window.removeEventListener("open-lead-modal", handler);
  }, []);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const close = () => setOpen(false);

  const submit = async () => {
    if (name.trim().length < 2) {
      setError("Dimmi come ti chiami.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Inserisci un'email valida.");
      return;
    }
    setError("");
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), phone: phone.trim(), interest, message: message.trim() }),
      });
      if (!res.ok) throw new Error();
      setDone(true);
    } catch {
      setError("Qualcosa non ha funzionato. Riprova tra poco.");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center sm:p-6"
          onClick={close}
        >
          <motion.div
            data-testid="lead-modal"
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto border border-[#383029] bg-[#12100E] p-7 sm:p-9"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="section-kicker">{interest || "Contatto"}</p>
                <p className="mt-2 font-heading text-2xl font-black uppercase tracking-tight text-[#FAF7F2]">
                  Lascia i tuoi contatti
                </p>
                <p className="mt-2 text-sm leading-relaxed text-[#A89F91]">
                  Ti ricontatto io, entro 48 ore lavorative. Niente spam, niente telefonate a sorpresa.
                </p>
              </div>
              <button data-testid="lead-close-button" onClick={close} aria-label="Chiudi" className="rounded-full border border-[#383029] p-2 text-[#A89F91] transition-colors duration-300 hover:border-[#E05A2C] hover:text-[#FAF7F2]">
                <X className="h-4 w-4" />
              </button>
            </div>

            {done ? (
              <div data-testid="lead-success" className="mt-8 border border-[#383029] p-6">
                <p className="font-heading text-lg font-black uppercase tracking-tight text-[#FAF7F2]">Ricevuto.</p>
                <p className="mt-2 text-sm text-[#C4BBB0]">Ti scriviamo entro 48 ore lavorative. Se è urgente, prenota direttamente una chiamata gratuita.</p>
              </div>
            ) : (
              <div className="mt-7 flex flex-col gap-3">
                <input data-testid="lead-name-input" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Come ti chiami" className={inputCls} />
                <input data-testid="lead-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="La tua email" className={inputCls} />
                <input data-testid="lead-phone-input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Telefono (facoltativo)" className={inputCls} />
                <select data-testid="lead-interest-select" value={interest} onChange={(e) => setInterest(e.target.value)} className={`${inputCls} appearance-none`}>
                  <option value="" disabled className="bg-[#12100E]">Cosa ti interessa?</option>
                  {INTERESTS.map((opt) => (
                    <option key={opt} value={opt} className="bg-[#12100E]">{opt}</option>
                  ))}
                </select>
                <textarea data-testid="lead-message-input" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Raccontami la tua struttura in due righe (facoltativo)" rows={3} className={`${inputCls} resize-none`} />
                {error && (
                  <p data-testid="lead-error" role="alert" aria-live="polite" className="border-l-2 border-[#E05A2C] pl-3 text-sm text-[#E05A2C]">
                    {error}
                  </p>
                )}
                <button
                  data-testid="lead-submit-button"
                  onClick={submit}
                  disabled={sending}
                  className="btn-glow mt-2 rounded-full bg-[#E05A2C] px-8 py-4 text-sm font-medium text-white transition-colors duration-300 hover:bg-[#F26B3A] active:scale-[0.98] disabled:opacity-50"
                >
                  {sending ? "Invio…" : "Invia — ti ricontatto io"}
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
